import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import useTranslation from "../../../hooks/useTranslation";
import { useGeneratedTextCreateSpeech } from "../../../hooks/useGeneratedTextUpdateSpeech";
import { useGeneratedTextDetails } from "../../../hooks/useGeneratedTextDetails";
import { useIncrementGeneratedTextViewCounter } from "../../../hooks/useIncrementGeneratedTextViewCounter";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  usePaginatedVocabularyList,
  VocabularyDto,
} from "../../../hooks/usePaginatedVocabularyList";
import { useChapterCreate } from "../../../hooks/useChapterCreate";
import { GeneratedTextDetailsTextPane } from "../../../components/GeneratedTextDetailsTextPane";
import { LanguageHelperPanel } from "../../../components/LanguageHelperPanel";
import { useVocabularyUpdate } from "../../../hooks/useVocabularyUpdate";
import { SubmitHandler, useForm } from "react-hook-form";
import { useChapterRetrieveQuery } from "../../../hooks/useChapterRetrieveQuery";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  getVocabularyBaseWord,
  getVocabularyPrimaryWord,
  getVocabularyPrimaryTranslation,
  getVocabularySurfaceWord,
  normalizeVocabularyTranslationDetails,
} from "../../../utils/vocabularyTranslationDetails";
import { highlightVocabularyWordInSentence } from "../../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../../components/VocabularyPronunciationButton";
import { resolveGeneratedTextContent } from "../../../utils/generatedTextContent";
import { resolveSelectedVocabularyWordsForChapterGeneration } from "../../../utils/resolveSelectedVocabularyWordsForChapterGeneration";
import { notifySnackbar } from "../../../lib/notifySnackbar";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";

export const Route = createFileRoute("/app/reeprise/$generatedTextId")({
  component: GeneratedTextDetailsPage,
  validateSearch: (search) => {
    return {
      chapter:
        search.chapter === undefined ? undefined : Number(search.chapter),
    };
  },
});

function GeneratedTextDetailsPage() {
  const { translate } = useTranslation();
  const { generatedTextId } = Route.useParams();
  const { chapter } = Route.useSearch();
  const { createSpeechMutation } = useGeneratedTextCreateSpeech();
  const reepriseDetails = useGeneratedTextDetails(Number(generatedTextId));
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const chapterDetails = useChapterRetrieveQuery({
    chapterCounter: chapter ?? 0,
    generatedTextId: Number(generatedTextId),
  });
  const incrementCounter = useIncrementGeneratedTextViewCounter({
    textId: Number(generatedTextId),
  });

  const topRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioProgressRef = useRef<HTMLDivElement | null>(null);

  const [selectedVocabularyIds, setSelectedVocabularyIds] = useState<number[]>(
    [],
  );
  const [deselectedVocabularyIds, setDeselectedVocabularyIds] = useState<
    number[]
  >([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isAudioScrubbing, setIsAudioScrubbing] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1);
  const showAudioLoading = isAudioLoading || createSpeechMutation.isPending;
  const [recentVocabulary, setRecentVocabulary] = useState<
    { word: string; translation?: string | null }[]
  >([]);
  const [optimisticSelectedWordsById, setOptimisticSelectedWordsById] = useState<
    Record<number, string>
  >({});
  const [titleOnlyVocabularyIds, setTitleOnlyVocabularyIds] = useState<number[]>([]);
  const [focusRequest, setFocusRequest] = useState<{
    word: string;
    token: number;
  } | null>(null);
  const audioSrc =
    chapter !== undefined
      ? `/streams/audio/${generatedTextId}/?chapter=${chapter}`
      : `/streams/audio/${generatedTextId}/`;

  const onVocabularyCreate = (
    vocabularyDto: VocabularyDto,
    options?: { source: "title" | "text" },
  ) => {
    notifySnackbar({ message: translate("word_added_toast") });
    setSelectedVocabularyIds((oldValue) => [...oldValue, vocabularyDto.id]);
    setDeselectedVocabularyIds((prev) =>
      prev.filter((entry) => entry !== vocabularyDto.id),
    );
    if (options?.source === "title") {
      setTitleOnlyVocabularyIds((prev) =>
        prev.includes(vocabularyDto.id) ? prev : [...prev, vocabularyDto.id],
      );
    }
    if (vocabularyDto.id && vocabularyDto.word?.trim()) {
      setOptimisticSelectedWordsById((prev) => ({
        ...prev,
        [vocabularyDto.id]: vocabularyDto.word.trim(),
      }));
    }
    if (vocabularyDto.word?.trim()) {
      setRecentVocabulary((prev) => [
        { word: vocabularyDto.word, translation: vocabularyDto.translation },
        ...prev.filter((item) => item.word !== vocabularyDto.word),
      ].slice(0, 5));
    }
  };

  useEffect(() => {
    const appContentScroller = document.querySelector<HTMLElement>(
      "[data-app-content-scroll]",
    );
    if (appContentScroller) {
      appContentScroller.scrollTop = 0;
    }
    setSelectedVocabularyIds([]);
    setDeselectedVocabularyIds([]);
    setOptimisticSelectedWordsById({});
    setTitleOnlyVocabularyIds([]);
  }, [generatedTextId, chapter]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setIsPlaying(false);
    setIsAudioLoading(false);
    setIsAudioScrubbing(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioPlaybackRate(1);
  }, [generatedTextId, chapter]);

  const chapterCreateMutation = useChapterCreate({
    generatedTextId: Number(generatedTextId),
  });

  const paginatedVocabularyList = usePaginatedVocabularyList({
    generated_text_id: Number(generatedTextId),
  });
  const navigate = useNavigate();

  useEffect(() => {
    const id = setTimeout(() => {
      void incrementCounter.mutateAsync();
    }, 5_000);

    return () => clearTimeout(id);
  }, [generatedTextId, incrementCounter]);

  const vocabularies =
    paginatedVocabularyList.data?.pages.flatMap((page) => page.results) || [];
  const resolvedDetailsTitle = resolveGeneratedTextContent({
    rawText:
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "",
    fallbackTitle: reepriseDetails.data?.topic ?? "",
    generatingTitleCopy: translate("text_card_generating_title"),
    isComplete: reepriseDetails.data?.status === "COMPLETE",
  }).displayTitle.trim();
  const persistedTitleOnlyVocabularyIds = useMemo(
    () =>
      vocabularies
        .filter((voc) => voc.sentence?.trim() === resolvedDetailsTitle)
        .map((voc) => voc.id),
    [resolvedDetailsTitle, vocabularies],
  );
  const selectedVocabularyWords = useMemo(
    () =>
      vocabularies
        .filter(
          (voc) =>
            selectedVocabularyIds.includes(voc.id) &&
            !titleOnlyVocabularyIds.includes(voc.id) &&
            !persistedTitleOnlyVocabularyIds.includes(voc.id),
        )
        .map((voc) => voc.word),
    [
      vocabularies,
      selectedVocabularyIds,
      titleOnlyVocabularyIds,
      persistedTitleOnlyVocabularyIds,
    ],
  );
  const selectedVocabularyWordsForGeneration = useMemo(
    () =>
      resolveSelectedVocabularyWordsForChapterGeneration({
        optimisticWordsById: optimisticSelectedWordsById,
        selectedVocabularyIds,
        vocabularies,
      }),
    [optimisticSelectedWordsById, selectedVocabularyIds, vocabularies],
  );
  const activeHighlightWords =
    chapter !== undefined &&
    chapterDetails.data &&
    chapterDetails.data.status !== "NON_EXISTENT"
      ? chapterDetails.data.generated_words
      : selectedVocabularyWords;
  const struggleVocabularyCards: VocabularyDto[] =
    reepriseDetails.isSuccess && Array.isArray(reepriseDetails.data?.struggle_words)
      ? reepriseDetails.data.struggle_words
          .filter(
            (entry) => entry.word_language === reepriseDetails.data?.language,
          )
          .map((entry, idx) => ({
            id: -1000 - idx,
            word: entry.word,
            word_language: entry.word_language,
            translation_language: entry.translation_language ?? "",
            translation: entry.translation,
            winning_streak: -1,
            last_tested: new Date().toISOString(),
            mnemonic: "",
            sentence: entry.sentence,
            status: "COMPLETE",
            origin: "STRUGGLE_WORD",
            usages: [],
          }))
      : [];
  const showStruggleWords = struggleVocabularyCards.length > 0;

  const seekAudioByClientX = (clientX: number) => {
    const progressEl = audioProgressRef.current;
    const audioEl = audioRef.current;
    if (!progressEl || !audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration) ? audioEl.duration : 0;
    if (duration <= 0) {
      return;
    }
    const bounds = progressEl.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
  };

  useEffect(() => {
    if (vocabularies.length === 0) {
      setSelectedVocabularyIds((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const nextSelectedVocabularyIds = vocabularies
      .map((entry) => entry.id)
      .filter((entry) => !deselectedVocabularyIds.includes(entry));
    setSelectedVocabularyIds((prev) => {
      if (
        prev.length === nextSelectedVocabularyIds.length &&
        prev.every((entry, index) => entry === nextSelectedVocabularyIds[index])
      ) {
        return prev;
      }
      return nextSelectedVocabularyIds;
    });
  }, [vocabularies, deselectedVocabularyIds]);

  useEffect(() => {
    if (!isAudioScrubbing) {
      return;
    }
    const onMouseMove = (event: MouseEvent) => {
      seekAudioByClientX(event.clientX);
    };
    const onMouseUp = () => {
      setIsAudioScrubbing(false);
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      seekAudioByClientX(touch.clientX);
    };
    const onTouchEnd = () => {
      setIsAudioScrubbing(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isAudioScrubbing]);

  const createChapterFromSelection = async () => {
    const words = selectedVocabularyWordsForGeneration;
    const createdChapter = await chapterCreateMutation.mutateAsync({
      words,
    });
    navigate({
      to: "/app/reeprise/$generatedTextId",
      params: {
        generatedTextId: generatedTextId,
      },
      search: {
        chapter: createdChapter.counter,
      },
    });
  };

  return (
    <>
      <div
        ref={topRef}
        className=" bg-neutral-50 px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-left md:h-full md:pb-0 place-items-center"
      >
        <div className="md:grid md:grid-cols-12 gap-11 md:h-full md:mr-10">
          <div className={"relative md:p-4 rounded-lg md:h-full md:col-span-6 md:col-start-3 mb-9"}>
            <div className="mt-2 mb-2 flex items-center justify-between text-slate-700">
              <LastChapterNavigationTile
                activeChapterNumber={chapter}
                generatedTextId={Number(generatedTextId)}
              />
              <NextChapterNavigationTile
                activeChapterNumber={chapter}
                generatedTextId={Number(generatedTextId)}
              />
            </div>
            <div className="mt-3 border-y border-slate-200 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    createSpeechMutation.mutate({
                      id: Number(generatedTextId),
                      chapter_number: chapter,
                    });
                    const el = audioRef.current;
                    if (!el) return;
                    if (isPlaying) {
                      el.pause();
                    } else {
                      setIsAudioLoading(true);
                      void el.play().catch((err) => {
                        console.error("Audio play error", err);
                        setIsAudioLoading(false);
                      });
                    }
                  }}
                  className="h-8 w-8 rounded-full border border-slate-300 bg-white text-slate-700 grid place-items-center hover:bg-slate-50"
                  aria-label={
                    isPlaying
                      ? translate("audio_player_pause")
                      : translate("audio_player_play")
                  }
                >
                  {isPlaying ? (
                    <PauseIcon className="h-4 w-4" />
                  ) : (
                    <PlayIcon className="h-4 w-4 pl-[1px]" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                    {translate("audio_player_title")}
                  </p>
                  <p className="font-serif text-sm text-slate-700 flex items-center gap-2">
                    {showAudioLoading && (
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                    )}
                    {audioDuration > 0
                      ? `${formatAudioClock(audioCurrentTime)} / ${formatAudioClock(audioDuration)}`
                      : showAudioLoading
                        ? translate("audio_player_loading")
                        : isPlaying
                          ? translate("audio_player_playing")
                          : translate("audio_player_idle")}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                  onClick={() => {
                    const nextRate =
                      audioPlaybackRate === 1
                        ? 1.25
                        : audioPlaybackRate === 1.25
                          ? 1.5
                          : 1;
                    const el = audioRef.current;
                    if (el) {
                      el.playbackRate = nextRate;
                    }
                    setAudioPlaybackRate(nextRate);
                  }}
                >
                  {`${audioPlaybackRate}x`}
                </button>
              </div>
              <div
                ref={audioProgressRef}
                className="relative mt-2 h-4 w-full cursor-pointer"
                onMouseDown={(event) => {
                  seekAudioByClientX(event.clientX);
                  setIsAudioScrubbing(true);
                }}
                onTouchStart={(event) => {
                  const touch = event.touches[0];
                  if (!touch) {
                    return;
                  }
                  seekAudioByClientX(touch.clientX);
                  setIsAudioScrubbing(true);
                }}
                role="slider"
                aria-label={translate("audio_player_title")}
                aria-valuemin={0}
                aria-valuemax={audioDuration || 0}
                aria-valuenow={audioCurrentTime}
              >
                <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-200" />
                <div
                  className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-slate-500 transition-[width] duration-200"
                  style={{
                    width:
                      audioDuration > 0
                        ? `${Math.min(100, (audioCurrentTime / audioDuration) * 100)}%`
                        : "0%",
                  }}
                />
                <button
                  type="button"
                  className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-400 bg-white shadow-sm hover:scale-105"
                  style={{
                    left:
                      audioDuration > 0
                        ? `${Math.min(100, (audioCurrentTime / audioDuration) * 100)}%`
                        : "0%",
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    seekAudioByClientX(event.clientX);
                    setIsAudioScrubbing(true);
                  }}
                  onTouchStart={(event) => {
                    event.stopPropagation();
                    const touch = event.touches[0];
                    if (!touch) {
                      return;
                    }
                    seekAudioByClientX(touch.clientX);
                    setIsAudioScrubbing(true);
                  }}
                  aria-label={translate("audio_player_title")}
                />
              </div>
              <audio
                key={audioSrc}
                ref={audioRef}
                src={audioSrc}
                preload="none"
                playsInline
                className="hidden"
                onPlay={() => {
                  // Keep loading state until audio is actually playing.
                }}
                onPlaying={() => {
                  setIsPlaying(true);
                  setIsAudioLoading(false);
                }}
                onTimeUpdate={(event) =>
                  setAudioCurrentTime(event.currentTarget.currentTime || 0)
                }
                onLoadedMetadata={(event) => {
                  setAudioDuration(event.currentTarget.duration || 0);
                  setAudioPlaybackRate(event.currentTarget.playbackRate || 1);
                }}
                onWaiting={() => setIsAudioLoading(true)}
                onStalled={() => setIsAudioLoading(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => {
                  setIsPlaying(false);
                  setAudioCurrentTime(audioDuration || 0);
                }}
                onError={() => setIsAudioLoading(false)}
              />
            </div>
            <GeneratedTextDetailsTextPane
              generatedTextId={Number(generatedTextId)}
              chapterNumber={chapter}
              onVocabularyCreate={onVocabularyCreate}
              highlightWords={activeHighlightWords}
              focusRequest={focusRequest}
            />
          </div>
          <div
            className={
              "bg-white-300 rounded-lg md:col-start-9 md:col-span-4 md:h-full "
            }
          >
            <div className="mb-4">
              <LanguageHelperPanel
                generatedTextId={Number(generatedTextId)}
                chapterNumber={chapter}
                wordLanguage={
                  languageSpace !== "ALL"
                    ? languageSpace
                    : reepriseDetails.data?.language
                }
                contextText={
                  chapter !== undefined
                    ? chapterDetails.data &&
                      "content" in chapterDetails.data &&
                      chapterDetails.data.status === "COMPLETE"
                      ? chapterDetails.data.content
                      : undefined
                    : reepriseDetails.data &&
                        "chatgpt_output" in reepriseDetails.data
                      ? reepriseDetails.data.chatgpt_output
                      : undefined
                }
                recentVocabulary={recentVocabulary}
              />
            </div>
            <h2 className="text-l bg-neutral-50 md:pt-5 font-semibold md:mb-1 mb-4 md:sticky top-0 z-10">
              {translate("vocabulary")}
            </h2>
            {vocabularies.length === 0 && (
              <div className="flex bg-slate-100 mb-4 p-3 rounded-md mt-4">
                <p>
                  <LightBulbIcon className="w-6 h-6 mr-1" />
                </p>
                {translate("translations_click_on_a_word_to_translate_it")}
              </div>
            )}
            {vocabularies.length > 0 && reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 hidden border-y border-slate-200 py-2 md:block">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    void createChapterFromSelection();
                  }}
                >
                  {translate("btn_Generate_new_Text")}
                </button>
              </div>
            )}
            <ul className="p-0 m-0 w-full z-20 md:pt-1 pb-24 md:pb-0">
              {showStruggleWords &&
                struggleVocabularyCards.map((arg) => (
                  <VocabularyItem
                    key={arg.id}
                    vocabulary={arg}
                    isSelected={false}
                    addOrRemoveVocabulary={() => {}}
                    onJump={() => {
                      setFocusRequest({
                        word: arg.word,
                        token: Date.now(),
                      });
                    }}
                  ></VocabularyItem>
                ))}
              {vocabularies.map((arg) => (
                <VocabularyItem
                  key={arg.id}
                  vocabulary={arg}
                  isSelected={selectedVocabularyIds.includes(arg.id)}
                  addOrRemoveVocabulary={() => {
                    if (selectedVocabularyIds.includes(arg.id)) {
                      setDeselectedVocabularyIds((prev) => [
                        ...prev.filter((entry) => entry !== arg.id),
                        arg.id,
                      ]);
                    } else {
                      setDeselectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                    }
                  }}
                  onJump={() => {
                    setFocusRequest({
                      word: arg.word,
                      token: Date.now(),
                    });
                  }}
                ></VocabularyItem>
              ))}
              {paginatedVocabularyList.hasNextPage && (
                <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
                  <button
                    className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => {
                      paginatedVocabularyList.fetchNextPage();
                    }}
                  >
                    Load more
                  </button>
                </li>
              )}
            </ul>
            {vocabularies.length > 0 && reepriseDetails.isSuccess && (
              <div className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2.5 text-[11px] uppercase tracking-[0.14em] text-slate-700"
                  onClick={() => {
                    void createChapterFromSelection();
                  }}
                >
                  {translate("btn_Generate_new_Text")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function formatAudioClock(rawSeconds: number) {
  if (!Number.isFinite(rawSeconds) || rawSeconds < 0) {
    return "0:00";
  }
  const totalSeconds = Math.floor(rawSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function VocabularyItem(props: {
  vocabulary: VocabularyDto;
  isSelected: boolean;
  addOrRemoveVocabulary: () => void;
  onJump: () => void;
}) {
  const [toggled, setToggled] = useState(false);
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v2",
    false,
  );
  const { translate } = useTranslation();
  const translationDetails = normalizeVocabularyTranslationDetails({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const primaryTranslation = getVocabularyPrimaryTranslation({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const primaryWord = getVocabularyPrimaryWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const surfaceWord = getVocabularySurfaceWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const baseWord = getVocabularyBaseWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const sourceSentence = props.vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;
  const hasExpandedGrammarDetails = Boolean(
    translationDetails.grammar.mood ||
      translationDetails.grammar.person ||
      translationDetails.grammar.case ||
      translationDetails.grammar.syntactic_function ||
      translationDetails.alternative_meanings ||
      translationDetails.source_lemma ||
      translationDetails.source_context_hint,
  );

  return (
    <li
      className={
        "group relative mb-4 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)] " +
        (toggled ? "" : "cursor-pointer")
      }
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("[data-vocabulary-control='true']")) {
          return;
        }
        if (!toggled) {
          props.onJump();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="m-0 px-1 font-serif text-xl font-semibold leading-snug text-slate-900">
            {primaryWord}
          </p>
          <div data-vocabulary-control="true">
            <VocabularyPronunciationButton vocabulary={props.vocabulary} />
          </div>
        </div>
        <input
          type="checkbox"
          data-vocabulary-control="true"
          className="appearance-none h-5 w-5 rounded-[6px] border border-slate-300 bg-white transition-colors duration-150 cursor-pointer relative
            hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2
            checked:bg-slate-900 checked:border-slate-900
            checked:after:content-[''] checked:after:absolute checked:after:w-[6px] checked:after:h-[10px] checked:after:border-r-[2px] checked:after:border-b-[2px] checked:after:border-white checked:after:rotate-45 checked:after:left-1/2 checked:after:top-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-[55%]"
          checked={props.isSelected}
          onChange={props.addOrRemoveVocabulary}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
      <p className="mb-2 mt-0 px-1 font-serif text-lg font-normal leading-snug text-slate-600">
        {primaryTranslation}
      </p>
      {surfaceWord !== primaryWord && (
        <p className="mb-2 px-1 text-xs text-slate-600">
          <span className="font-semibold">
            {translate("vocabulary_label_seen_form")}:
          </span>{" "}
          {surfaceWord}
        </p>
      )}
      <div className="mt-3 border-y border-slate-200 py-2 space-y-1">
        <p className="m-0 text-left font-serif text-sm font-normal leading-snug text-slate-700">
          {primarySentence
            ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <p className="m-0 text-left font-serif text-sm font-normal leading-snug text-slate-500">
            {secondarySentence}
          </p>
        )}
      </div>
      <div className="mt-3 border-y border-slate-200 py-2 space-y-2">
        {baseWord && (
          <p className="px-1 text-xs text-slate-600">
            <span className="font-semibold">
              {translate("vocabulary_label_base_form")}:
            </span>{" "}
            {baseWord}
          </p>
        )}
        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
          {translate("vocabulary_label_grammar")}
        </p>
        <div className="flex flex-wrap gap-2 text-xs">
          {translationDetails.grammar.part_of_speech && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_pos")}:{" "}
              {translationDetails.grammar.part_of_speech}
            </span>
          )}
          {translationDetails.grammar.tense && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_tense")}:{" "}
              {translationDetails.grammar.tense}
            </span>
          )}
          {translationDetails.grammar.number && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_number")}:{" "}
              {translationDetails.grammar.number}
            </span>
          )}
          {translationDetails.grammar.gender && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_gender")}:{" "}
              {translationDetails.grammar.gender}
            </span>
          )}
          {translationDetails.source_plural_form && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_plural")}:{" "}
              {translationDetails.source_plural_form}
            </span>
          )}
        </div>
        {showMoreGrammar && hasExpandedGrammarDetails && (
          <div className="grid gap-1 text-xs text-slate-600">
            {translationDetails.grammar.mood && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_mood")}:
                </span>{" "}
                {translationDetails.grammar.mood}
              </p>
            )}
            {translationDetails.grammar.person && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_person")}:
                </span>{" "}
                {translationDetails.grammar.person}
              </p>
            )}
            {translationDetails.grammar.case && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_case")}:
                </span>{" "}
                {translationDetails.grammar.case}
              </p>
            )}
            {translationDetails.grammar.syntactic_function && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_syntactic_function")}:
                </span>{" "}
                {translationDetails.grammar.syntactic_function}
              </p>
            )}
            {translationDetails.alternative_meanings && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_alternatives")}:
                </span>{" "}
                {translationDetails.alternative_meanings}
              </p>
            )}
            {translationDetails.source_lemma &&
              translationDetails.source_lemma !== primaryWord &&
              translationDetails.source_lemma !== baseWord && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_lemma")}:
                  </span>{" "}
                  {translationDetails.source_lemma}
                </p>
              )}
            {translationDetails.source_context_hint && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_context_hint")}:
                </span>{" "}
                {translationDetails.source_context_hint}
              </p>
            )}
          </div>
        )}
        {hasExpandedGrammarDetails && (
          <div className="flex justify-end">
            <button
              type="button"
              data-vocabulary-control="true"
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                setShowMoreGrammar((prev) => !prev);
              }}
              className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 focus-visible:outline-none"
            >
              {showMoreGrammar
                ? translate("vocabulary_less_grammar")
                : translate("vocabulary_more_grammar")}
            </button>
          </div>
        )}
      </div>
      

      {toggled ? (
        <MnemonicEditForm vocabulary={props.vocabulary} />
      ) : (
        <p className="mt-2 px-1 font-serif text-base font-normal leading-snug text-slate-700">
          {props.vocabulary.mnemonic}
        </p>
      )}
      <div className="flex justify-between my-2">
        <button
          data-vocabulary-control="true"
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-base hover:bg-slate-50"
          onClick={(event) => {
            event.stopPropagation();
            setToggled(!toggled);
          }}
        >
          {toggled ? "❌" : "🖊️"}
        </button>
      </div>
    </li>
  );
}

type MnemonicFormValues = {
  mnemonic: string;
};

function MnemonicEditForm(props: { vocabulary: VocabularyDto }) {
  const updateVocabulary = useVocabularyUpdate();
  const { register, handleSubmit } = useForm<MnemonicFormValues>();
  const onSubmit: SubmitHandler<MnemonicFormValues> = (data) => {
    void updateVocabulary.mutateAsync({
      id: props.vocabulary.id,
      patchVocabularyDto: {
        mnemonic: data.mnemonic,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex justify-between items-center"
    >
      <textarea
        {...register("mnemonic")}
        rows={5}
        cols={40}
        className="grow p-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
        defaultValue={props.vocabulary.mnemonic ?? ""}
      ></textarea>
      <input
        type="submit"
        value="☑️"
        className="m-2 text-2xl border-gray-300"
      />
    </form>
  );
}

function LastChapterNavigationTile(props: {
  activeChapterNumber: number | undefined;
  generatedTextId: number;
}) {
  const { translate } = useTranslation();
  const previousChapterNumber =
    props.activeChapterNumber === undefined
      ? undefined
      : props.activeChapterNumber - 1;

  if (previousChapterNumber !== undefined) {
    return (
      <Link
        to="/app/reeprise/$generatedTextId"
        params={{
          generatedTextId: props.generatedTextId.toString(),
        }}
        search={{
          chapter:
            previousChapterNumber === -1 ? undefined : previousChapterNumber,
        }}
        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        <span>{translate("chapter_previous")}</span>
      </Link>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">
      <ChevronLeftIcon className="h-4 w-4" />
      <span>{translate("chapter_previous")}</span>
    </div>
  );
}

function NextChapterNavigationTile(props: {
  activeChapterNumber: number | undefined;
  generatedTextId: number;
}) {
  const { translate } = useTranslation();
  const nextChapterNumber =
    props.activeChapterNumber === undefined ? 0 : props.activeChapterNumber + 1;

  const nextChapterQuery = useChapterRetrieveQuery({
    chapterCounter: nextChapterNumber,
    generatedTextId: props.generatedTextId,
  });

  if (
    nextChapterQuery.data !== undefined &&
    nextChapterQuery.data.status !== "NON_EXISTENT"
  ) {
    return (
      <Link
        to="/app/reeprise/$generatedTextId"
        params={{
          generatedTextId: props.generatedTextId.toString(),
        }}
        search={{
          chapter: nextChapterNumber,
        }}
        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
      >
        <span>{translate("chapter_next")}</span>
        <ChevronRightIcon className="h-4 w-4" />
      </Link>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">
      <span>{translate("chapter_next")}</span>
      <ChevronRightIcon className="h-4 w-4" />
    </div>
  );
}
